import { projectListId } from "../../core/code/loader";
import { CONSTANTS, EDITOR_VIEW_NAME, LAUNCH_VIEW_NAME } from "../../core/constants";
import { translate } from "../../core/i18n";
import { getIconForProject } from "../../core/project/projectslist";
import { getDomainCfg } from "../../core/state";
import { LocalStorageData, ProjectListEntry } from "../../core/types";
import { getViewData, setViewData } from "../../core/views";
import { executeUserCode, getLauncherCode } from "../coderunner";
import { decompressBase64, decompressBase64Async } from "../zipping";
import { getUserDataKey } from "./keys";
import { saveProjectInLocalProjectList } from "./saver";

export const hasLocalUserData = () : boolean => {
  const ckData = localStorage.getItem(getUserDataKey(undefined));
  return ckData != null;
}


const loadUserState = async (localStorageData : LocalStorageData) : Promise<void> => {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  
  // Prüfe ob V2-Projekt (Meta-Daten ohne code/sources)
  const isV2Meta = (localStorageData as any).isV2 === true;
  
  let newState = { ...editorState,
    project : {
      ...editorState.project,
      project: localStorageData.projectName,
      hint: localStorageData.projectHint,
      task: localStorageData.projectTask
    }
  };
  
  if (isV2Meta) {
    // V2: Nur Meta-Daten laden, KEINE code/sources (die kommen aus VFS)
    newState.data = {
      ...editorState.data,
      version : localStorageData.version,
      // KEINE editorCode, editorTabs für V2!
      userAssets : localStorageData.assets,
      projectFinished : localStorageData.projectFinished,
      projectProgress : localStorageData.projectProgress,
      chatMessages: localStorageData.chatMessages || [],
      agentChatMessages: (localStorageData as any).agentChatMessages || [],
      agentChatId: (localStorageData as any).agentChatId,
      lastUsedModelId: localStorageData.lastUsedModelId,
      lastVfsWriteAt: (localStorageData as any).lastVfsWriteAt,
    }
    // Für V2: Kein executeUserCode hier - das passiert beim projectloader
    setViewData(EDITOR_VIEW_NAME, newState);
  } else {
    // Legacy: Alles laden (code + sources + meta)
    newState.data = {
      ...editorState.data,
      version : localStorageData.version,
      editorCode : localStorageData.code,
      editorTabs : localStorageData.sources,
      userAssets : localStorageData.assets,
      projectFinished : localStorageData.projectFinished,
      projectProgress : localStorageData.projectProgress,
      chatMessages: localStorageData.chatMessages || [],
      agentChatMessages: (localStorageData as any).agentChatMessages || [],
      agentChatId: (localStorageData as any).agentChatId,
      lastUsedModelId: localStorageData.lastUsedModelId
    }
    const preparedExecutionCode = await executeUserCode(newState, false);
    newState = { ...newState };
    newState.data = {
      ...newState.data,
      executionCode : preparedExecutionCode
    }  

    setViewData(EDITOR_VIEW_NAME, newState);
    
    const codeToLaunch = getLauncherCode(newState);
    const launchState = getViewData(LAUNCH_VIEW_NAME);
    const newLaunchState = {
      ...launchState,
      showCodeFrame: true,
      code : codeToLaunch
    }

    setViewData(LAUNCH_VIEW_NAME, newLaunchState)
  }
}

export const loadUserCodeFromLS = async () : Promise<void> => {
  const userJson = localStorage.getItem(getUserDataKey(undefined));
  if (userJson == null) {
    return;
  }

  if (testJSON(userJson)) {
    const userData = JSON.parse(userJson);
    // TODO: check why we only save user-property in here!?
    const localStorageData : LocalStorageData = userData;

    await loadUserState(localStorageData);
  } else {
    decompressBase64(userJson, async (decompressed) => {
      const userData = JSON.parse(decompressed);
      const localStorageData : LocalStorageData = userData;

      await loadUserState(localStorageData);
    });
  }

}

export function getUserCodeFromLS(projectCode : string, loadedCallback : (data : any) => void) {
  const userJson = localStorage.getItem(getUserDataKey(projectCode));
  if (userJson == null) {
    return;
  }

  if (testJSON(userJson)) {
    const userData = JSON.parse(userJson);
    loadedCallback(userData);
  } else {
    decompressBase64(userJson, function(decompressed) {
      const userData = JSON.parse(decompressed);
      loadedCallback(userData);
    });
  }
}

export async function getUserCodeFromLSAsync(projectCode: string): Promise<any> {
  const userJson = localStorage.getItem(getUserDataKey(projectCode));
  if (userJson == null) {
    return null;
  }

  return new Promise<any>((resolve) => {
    if (testJSON(userJson)) {
      const userData = JSON.parse(userJson);
      resolve(userData);
    } else {
      decompressBase64Async(userJson, (decompressed) => {
        const userData = JSON.parse(decompressed);
        resolve(userData);
      });
    }
  });
}

export function loadProjectFromLS(key : string, success : (data : any) => void) {
  const userJson = localStorage.getItem(key);
  if (userJson == null) {
    return;
  }

  if (testJSON(userJson)) {
    const userData = JSON.parse(userJson);
    if (success !== undefined)  {
      success(userData);
    }
  } else {
    decompressBase64(userJson, function(decompressed) {      
      const userData = JSON.parse(decompressed);
      if (success !== undefined)  {
        success(userData);
      }
    });
  }
}

export function loadLocalProjectList() {
  
  const list = getLocalStorageProjectList();
  const prefix = getDomainCfg().viewSettings.codeId;
  for (let i = 0; i < localStorage.length; i++) {
    const projectId = localStorage.key(i) || "";
    if (projectId.substring(0,3 + prefix.length) === CONSTANTS.LS_PREFIX + prefix) {
      const cardId = projectId.replace(CONSTANTS.LS_PREFIX + prefix,'');
      const newProjectCard : ProjectListEntry = {
        id : cardId,
        name : translate("view-projects-card", cardId),
        screenshot:null,
        icon: getIconForProject(cardId),
        status:"private"
      }
      if (cardId === "ONBO") continue;
      const foundIndex = list.findIndex((item) => item.id === cardId);
      if (foundIndex == -1) {
        list.push(newProjectCard);
      }
      
    }
  }
  return list;
}

function testJSON(text : string) {
  if (typeof text!=="string") {
      return false;
  }

  try {
      JSON.parse(text);
      return true;
  } catch (error) {
      return false;
  }
}

export function getLocalStorageProjectList() {
  const item = localStorage.getItem(projectListId());
  if (item && item.length > 0) {
    const projectList = JSON.parse(item);
    if (projectList && projectList.length > 0) {
      return projectList;
    }
  }
  return [];
}